import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import cognitoConfig, { ecapCognitoConfig } from './cognitoConfig';

const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.UserPoolId,
  ClientId: cognitoConfig.ClientId,
});

// The Movorra ECAP dashboard uses its OWN pool. Sessions are keyed by client id in localStorage, so an ECAP
// login and an existing (ride-share) login coexist without colliding — zero impact on existing features.
const ecapPool = new CognitoUserPool({
  UserPoolId: ecapCognitoConfig.UserPoolId,
  ClientId: ecapCognitoConfig.ClientId,
});

const mapSession = (session) => ({
  idToken: session.getIdToken().getJwtToken(),
  accessToken: session.getAccessToken().getJwtToken(),
  refreshToken: session.getRefreshToken().getToken(),
  user: session.getIdToken().payload,
});

function poolSignIn(pool, username, password) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: username, Pool: pool });
    const authDetails = new AuthenticationDetails({ Username: username, Password: password });
    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve(mapSession(session)),
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        reject({ code: 'NewPasswordRequired', userAttributes, cognitoUser: user });
      },
    });
  });
}

// completeNewPassword doesn't need the pool — the cognitoUser already carries its own pool.
function completeChallenge(cognitoUser, newPassword) {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => resolve(mapSession(session)),
      onFailure: (err) => reject(err),
    });
  });
}

// Forgot-password flow. Also the only way out of RESET_REQUIRED (an admin clicked "reset password"): Cognito
// rejects sign-in with PasswordResetRequiredException until a code + new password is confirmed here.
// Resolves with the masked delivery destination (e.g. "g***@g***") when Cognito reports one.
function requestReset(pool, username) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: username, Pool: pool });
    user.forgotPassword({
      onSuccess: (data) => resolve(data?.CodeDeliveryDetails?.Destination || null),
      onFailure: (err) => reject(err),
    });
  });
}

function confirmReset(pool, username, code, newPassword) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: username, Pool: pool });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

function poolSignOut(pool) {
  const user = pool.getCurrentUser();
  if (user) {
    user.signOut();
  }
}

function poolSession(pool) {
  return new Promise((resolve, reject) => {
    const user = pool.getCurrentUser();
    if (!user) {
      reject(new Error('No user signed in'));
      return;
    }
    user.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(mapSession(session));
    });
  });
}

// --- Existing pool (ride-share customer/driver/ops) — behaviour unchanged ---
export const signIn = (username, password) => poolSignIn(userPool, username, password);
export const completeNewPassword = (cognitoUser, newPassword) => completeChallenge(cognitoUser, newPassword);
export const requestPasswordReset = (username) => requestReset(userPool, username);
export const confirmPasswordReset = (username, code, newPassword) => confirmReset(userPool, username, code, newPassword);
export const signOut = () => poolSignOut(userPool);
export const getCurrentSession = () => poolSession(userPool);

// --- Movorra ECAP pool (the dashboard only) ---
export const signInEcap = (username, password) => poolSignIn(ecapPool, username, password);
export const completeNewPasswordEcap = (cognitoUser, newPassword) => completeChallenge(cognitoUser, newPassword);
export const requestPasswordResetEcap = (username) => requestReset(ecapPool, username);
export const confirmPasswordResetEcap = (username, code, newPassword) => confirmReset(ecapPool, username, code, newPassword);
export const signOutEcap = () => poolSignOut(ecapPool);
export const getCurrentEcapSession = () => poolSession(ecapPool);
